<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import {
  checkFileSizeLimit,
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
} from 'shared/helpers/FileHelper';
import {
  hasPressedCommandAndEnter,
  hasPressedEnterAndNotCmdOrShift,
} from 'shared/helpers/KeyboardHelpers';
import { quoteText } from '../helpers/timeline';

const props = defineProps({
  channelId: { type: Number, required: true },
  placeholder: { type: String, default: '' },
  isSending: { type: Boolean, default: false },
  // Message being answered (WhatsApp-style quote), or null.
  replyTo: { type: Object, default: null },
});

const emit = defineEmits(['send', 'typing', 'cancelReply']);

// "Typing" follows the draft itself: text appears → typing; 4 s without
// changes or an empty box → stopped. While it lasts it is repeated so it never
// expires on the other side (the store drops it after 8 s without news).
const TYPING_IDLE_MS = 4000;
const TYPING_REFRESH_MS = 5000;
let typingRefresh = null;
let typingIdle = null;

const startTyping = () => {
  emit('typing', { channelId: props.channelId, typing: true });
  clearInterval(typingRefresh);
  typingRefresh = setInterval(
    () => emit('typing', { channelId: props.channelId, typing: true }),
    TYPING_REFRESH_MS
  );
};
const stopTyping = (channelId = props.channelId) => {
  clearTimeout(typingIdle);
  if (!typingRefresh) return;
  clearInterval(typingRefresh);
  typingRefresh = null;
  emit('typing', { channelId, typing: false });
};

const { t } = useI18n();

const draft = ref('');
const editorKey = ref(0);
const files = ref([]);
const fileInputRef = ref(null);
// While the @mention list is open, Enter picks a person instead of sending.
const isMentionMenuOpen = ref(false);

const canSend = computed(
  () => (draft.value.trim() || files.value.length) && !props.isSending
);

const previews = computed(() =>
  files.value.map(file => ({
    file,
    url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
  }))
);
watch(previews, (current, previous = []) =>
  previous.forEach(item => item.url && URL.revokeObjectURL(item.url))
);
onBeforeUnmount(() => {
  previews.value.forEach(item => item.url && URL.revokeObjectURL(item.url));
  stopTyping();
});

watch(draft, value => {
  if (!value.trim()) {
    stopTyping();
    return;
  }
  if (!typingRefresh) startTyping();
  clearTimeout(typingIdle);
  typingIdle = setTimeout(() => stopTyping(), TYPING_IDLE_MS);
});

const reset = () => {
  draft.value = '';
  files.value = [];
  editorKey.value += 1;
};

// A draft or attachment never travels to another channel, and the old
// channel learns that we stopped typing there.
watch(
  () => props.channelId,
  (current, previous) => {
    stopTyping(previous);
    reset();
  }
);

const send = () => {
  if (!canSend.value) return;
  const payload = {
    content: draft.value.trim(),
    files: files.value,
    replyToId: props.replyTo?.id || null,
  };
  stopTyping();
  reset();
  emit('send', payload, {
    // The page puts the text back if the server refuses it.
    restore: () => {
      draft.value = payload.content;
      files.value = payload.files;
      editorKey.value += 1;
    },
  });
};

// Chat rule, whatever the agent chose for conversations: Enter sends and
// Shift+Enter breaks the line (Ctrl+Enter also sends). Runs in the capture
// phase so the editor never turns that Enter into a new line.
const onKeydown = event => {
  if (event.key === 'Escape' && props.replyTo) {
    emit('cancelReply');
    return;
  }
  if (isMentionMenuOpen.value || event.isComposing) return;
  if (!hasPressedEnterAndNotCmdOrShift(event) && !hasPressedCommandAndEnter(event)) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  send();
};

const onPickFiles = event => {
  const valid = Array.from(event.target.files || []).filter(file => {
    if (checkFileSizeLimit(file, DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE)) return true;
    useAlert(
      t('TEAM_CHAT.FILE_TOO_LARGE', { size: DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE })
    );
    return false;
  });
  files.value = [...files.value, ...valid];
  event.target.value = '';
};

const rootRef = ref(null);
const focus = () => rootRef.value?.querySelector('.ProseMirror')?.focus();
defineExpose({ focus });

const replyName = computed(
  () => props.replyTo?.user?.available_name || props.replyTo?.user?.name || ''
);
const replySnippet = computed(() => {
  if (!props.replyTo) return '';
  const text = quoteText(props.replyTo.content);
  return text || `📎 ${t('TEAM_CHAT.ATTACHMENT')}`;
});

const removeFile = index => {
  files.value = files.value.filter((_, i) => i !== index);
};
</script>

<template>
  <!-- On phones the KALL-AI menu button floats at the bottom-left corner. -->
  <div ref="rootRef" class="px-3 pb-20 md:px-5 md:pb-4">
    <div
      class="transition-colors border shadow-sm rounded-2xl bg-n-solid-1 border-n-strong focus-within:border-n-slate-8"
      @keydown.capture="onKeydown"
    >
      <div
        v-if="replyTo"
        class="flex items-center gap-2 py-2 mx-3 mt-3 border-s-[3px] rounded-md ps-2.5 pe-1 bg-n-alpha-1 border-n-brand"
      >
        <span class="flex-1 min-w-0 text-xs">
          <span class="block font-semibold text-n-slate-12">
            {{ t('TEAM_CHAT.REPLYING_TO', { name: replyName }) }}
          </span>
          <span class="block truncate text-n-slate-11">{{ replySnippet }}</span>
        </span>
        <button
          type="button"
          class="flex items-center justify-center flex-shrink-0 rounded-md size-7 text-n-slate-11 hover:bg-n-alpha-2"
          :title="t('TEAM_CHAT.CANCEL_REPLY')"
          @click="emit('cancelReply')"
        >
          <span class="size-4 i-lucide-x" />
        </button>
      </div>
      <div v-if="previews.length" class="flex flex-wrap gap-2 px-3 pt-3">
        <div
          v-for="(item, index) in previews"
          :key="index"
          class="relative overflow-hidden border rounded-xl border-n-weak bg-n-alpha-1"
        >
          <img
            v-if="item.url"
            :src="item.url"
            :alt="item.file.name"
            class="object-cover size-16"
          />
          <div
            v-else
            class="flex items-center gap-2 py-2 pl-2 pr-7 max-w-[13rem] h-16"
          >
            <span
              class="flex items-center justify-center flex-shrink-0 rounded-lg size-8 bg-n-ruby-3 text-n-ruby-11"
            >
              <span class="size-4 i-lucide-file-text" />
            </span>
            <span class="text-xs truncate text-n-slate-12">
              {{ item.file.name }}
            </span>
          </div>
          <button
            type="button"
            class="absolute flex items-center justify-center text-white rounded-full top-1 end-1 size-5 bg-n-slate-12/70 hover:bg-n-slate-12"
            :title="t('TEAM_CHAT.REMOVE_FILE')"
            @click="removeFile(index)"
          >
            <span class="size-3 i-lucide-x" />
          </button>
        </div>
      </div>

      <WootMessageEditor
        :key="editorKey"
        v-model="draft"
        is-private
        enable-suggestions
        :enable-canned-responses="false"
        editor-id="team-chat-composer"
        class="px-4 pt-2 text-sm min-h-[2.75rem] max-h-60 overflow-y-auto text-n-slate-12 [&_.ProseMirror-menubar]:!hidden"
        :placeholder="placeholder"
        @toggle-user-mention="isMentionMenuOpen = $event"
      />

      <div class="flex items-center gap-1 px-2 pb-2">
        <input
          ref="fileInputRef"
          type="file"
          multiple
          class="hidden"
          @change="onPickFiles"
        />
        <button
          type="button"
          class="flex items-center justify-center rounded-lg size-8 text-n-slate-11 hover:bg-n-alpha-2"
          :title="t('TEAM_CHAT.ATTACH')"
          @click="fileInputRef?.click()"
        >
          <span class="size-4 i-lucide-paperclip" />
        </button>
        <span class="hidden ms-auto me-2 text-xs sm:inline text-n-slate-10">
          {{ t('TEAM_CHAT.ENTER_TO_SEND') }}
        </span>
        <button
          type="button"
          class="flex items-center justify-center text-white transition-opacity rounded-xl size-9 bg-n-brand ms-auto sm:ms-0 disabled:opacity-40"
          :disabled="!canSend"
          :title="t('TEAM_CHAT.SEND')"
          @click="send"
        >
          <span class="size-4 i-lucide-send-horizontal" />
        </button>
      </div>
    </div>
  </div>
</template>
